import { Query } from '@equinor/fusion-query';
import { firstValueFrom, from, lastValueFrom, map, } from 'rxjs';
import { jsonSelector } from '@equinor/fusion-framework-module-http/selectors';
import { ApiServices } from './api-services';
/** Cache key used by the internal {@link Query} instance. */
const queryKey = 'services';
/**
 * Transforms a fetch {@link Response} into an observable that emits a
 * validated array of {@link Service} objects.
 *
 * The response body is parsed as JSON, validated against the
 * {@link ApiServices} Zod schema, and optionally piped through a
 * `postProcess` operator (used for session overrides).
 *
 * @param response - Raw fetch response from the service discovery API.
 * @param postProcess - Optional RxJS operator to transform the parsed list.
 * @returns An `ObservableInput` emitting the final `Service[]`.
 */
const serviceResponseSelector = (response, postProcess) => {
    // parse response by using the jsonSelector
    const result$ = from(jsonSelector(response)).pipe(
    // parse and validate the response
    map((value) => ApiServices.default([]).parse(value)));
    // Only apply a transform pipeline when the caller supplied one
    if (postProcess) {
        // Apply the caller-supplied operator (e.g. session overrides) before emitting
        return result$.pipe(postProcess);
    }
    return result$;
};
/**
 * Default service discovery client that fetches endpoints from a remote API,
 * validates the response with Zod, caches results via {@link Query}, and
 * supports `sessionStorage`-based overrides.
 *
 * @remarks
 * Results are cached for 5 minutes. When `allow_cache` is `true`,
 * {@link resolveServices} returns the first cached value immediately;
 * otherwise it waits for the latest API response.
 */
export class ServiceDiscoveryClient {
    #query;
    /** Sub-path appended to the HTTP client's base URI when fetching services. */
    endpoint;
    /** HTTP client used to call the service discovery API. */
    http;
    /**
     * @param options - Construction options including the HTTP client,
     *   optional endpoint path, and optional post-processing operator.
     */
    constructor(options) {
        this.http = options.http;
        this.endpoint = options.endpoint;
        const postProcess = options.postProcess;
        // setup api handler (queue and cache)
        this.#query = new Query({
            client: {
                fn: () => this.http.fetch$(this.endpoint ?? '', {
                    selector: (response) => serviceResponseSelector(response, postProcess),
                }),
            },
            key: () => queryKey,
            // Cache for 5 minutes
            expire: 5 * 60 * 1000,
        });
    }
    /** {@inheritDoc IServiceDiscoveryClient.resolveServices} */
    resolveServices(allow_cache) {
        // firstValueFrom returns the cached snapshot; lastValueFrom waits for
        // the freshest value from the query
        const fn = allow_cache ? firstValueFrom : lastValueFrom;
        return fn(Query.extractQueryValue(this.#query.query()));
    }
    /** {@inheritDoc IServiceDiscoveryClient.resolveService} */
    async resolveService(key, allow_cache) {
        const services = await this.resolveServices(allow_cache);
        // Locate the service matching the requested key
        const service = services.find((s) => s.key === key);
        // Fail loudly when the requested key doesn't resolve to a known service
        if (!service) {
            throw Error(`Failed to resolve service, invalid key [${key}]`);
        }
        return service;
    }
}
//# sourceMappingURL=client.js.map